import classNames from 'classnames'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { FeaturedProject } from '@/types/codegen-contentful'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { FeaturedProjectBackground } from './FeaturedProjectBackground'
import { FeaturedProjectCallToAction } from './FeaturedProjectCallToAction'
import { FeaturedProjectExperimentWrapper } from './FeaturedProjectExperimentWrapper'
import { assignBackgroundImage } from './FeaturedProjectUtils'
import { FeaturedProjectVideoBackground } from './FeaturedProjectVideoBackground'

export interface FeaturedProjectProps {
  featuredProject: FeaturedProject
}

export const FeaturedProjectDisplay: React.FC<FeaturedProjectProps> = ({ featuredProject }) => {
  const {
    backgroundImageExperiment,
    ctaPosition,
    desktopBackgroundUrl,
    mobileBackgroundUrl,
    tabletBackgroundUrl,
    ultrawideBackgroundUrl,
    projectSlug,
  } = featuredProject

  const { asPath } = useSafeRouter()
  const tablet = tabletBackgroundUrl ?? desktopBackgroundUrl
  const showDavidVideo = projectSlug === slugs.david

  const backgrounds = {
    mobile: mobileBackgroundUrl as string,
    tablet: tablet as string,
    desktop: desktopBackgroundUrl as string,
    ultra: ultrawideBackgroundUrl as string,
    gradient: true,
    gravity: 'g_face',
  }

  const backgroundImage = assignBackgroundImage(backgrounds)
  const shorterDisplay = asPath.includes('/watch/dry-bar-best-of')
  const backgroundVideo =
    'https://content.angelstudios.com/david/David_tLoopingVideo_l-en_wNOCAPS_16x9_f23_y20251013_iDG.mp4'

  return (
    <>
      {!showDavidVideo && (
        <div
          className={classNames(
            'relative bg-transparent',
            shorterDisplay
              ? 'h-[70vh] lg:min-h-[400px] lg:h-[675px] lg:max-w-[1200px] lg:mx-auto lg:rounded-2xl lg:overflow-hidden'
              : 'h-[90vh] lg:min-h-[1000px] shortDesktop:min-h-[800px]',
          )}
        >
          {backgroundImageExperiment ? (
            <FeaturedProjectExperimentWrapper featuredProject={featuredProject} />
          ) : (
            <div className="relative h-full w-full">
              <FeaturedProjectBackground backgroundImage={backgroundImage} />
              <FeaturedProjectCallToAction
                featuredProject={featuredProject}
                position={ctaPosition ?? 'left'}
                shorterDisplay={shorterDisplay}
              />
            </div>
          )}
        </div>
      )}
      {showDavidVideo && (
        <GBFadeInExperiment experimentName="david-featured-project-video-background" defaultValue={false}>
          <ExperimentVariation variation={true}>
            <div
              className={classNames(
                'relative bg-transparent',
                shorterDisplay
                  ? 'h-[70vh] lg:min-h-[400px] lg:h-[675px] lg:max-w-[1200px] lg:mx-auto lg:rounded-2xl lg:overflow-hidden'
                  : 'h-[80vh] lg:min-h-[850px] shortDesktop:min-h-[600px]',
              )}
            >
              {backgroundImageExperiment ? (
                <FeaturedProjectExperimentWrapper featuredProject={featuredProject} />
              ) : (
                <div className="relative h-full w-full">
                  <FeaturedProjectVideoBackground backgroundVideo={backgroundVideo} />
                  <FeaturedProjectCallToAction
                    featuredProject={featuredProject}
                    position={ctaPosition ?? 'left'}
                    shorterDisplay={false}
                    videoBackground
                  />
                </div>
              )}
            </div>
          </ExperimentVariation>
          <ExperimentVariation variation={false}>
            <div
              className={classNames(
                'relative bg-transparent',
                shorterDisplay
                  ? 'h-[70vh] lg:min-h-[400px] lg:h-[675px] lg:max-w-[1200px] lg:mx-auto lg:rounded-2xl lg:overflow-hidden'
                  : 'h-[90vh] lg:min-h-[1000px] shortDesktop:min-h-[800px]',
              )}
            >
              {backgroundImageExperiment ? (
                <FeaturedProjectExperimentWrapper featuredProject={featuredProject} />
              ) : (
                <div className="relative h-full w-full">
                  <FeaturedProjectBackground backgroundImage={backgroundImage} />
                  <FeaturedProjectCallToAction
                    featuredProject={featuredProject}
                    position={ctaPosition ?? 'left'}
                    shorterDisplay={shorterDisplay}
                  />
                </div>
              )}
            </div>
          </ExperimentVariation>
        </GBFadeInExperiment>
      )}
    </>
  )
}
